import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Check, FileText, Loader2, Network, Search, Star, Users } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
  resourceProfilesApi,
  type ProfileGroupPayload,
  type ResourceSummary,
} from '@/api/resourceCenterApi'
import { useAssignmentGroups, useInvalidateResourceProfiles } from '@/hooks/useResourceCenter'
import { sameMemberships, setPrimary, toggleMembership } from '@/lib/assignmentGroups'

/**
 * One person's groups (z8nrz7f0vw, deck slide 3). Tap a group to add or
 * remove it; the star marks their Primary group, the rest are Secondary.
 * Their teams follow from the groups (z8nrz7f7dk).
 *
 * Keyed by person by its parent, so the draft starts from the saved list each
 * time someone is opened.
 */
export function PersonGroupsDialog({
  person,
  canManage,
  onOpenChange,
}: {
  person: ResourceSummary | null
  canManage: boolean
  onOpenChange: (open: boolean) => void
}) {
  const groups = useAssignmentGroups()
  const invalidate = useInvalidateResourceProfiles()
  const saved: ProfileGroupPayload[] = person?.groups.map(g => ({ groupId: g.id, isPrimary: g.isPrimary })) ?? []
  const [draft, setDraft] = useState<ProfileGroupPayload[]>(saved)
  const [search, setSearch] = useState('')
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: () => resourceProfilesApi.setGroups(person!.userId, draft),
    onSuccess: () => {
      void invalidate()
      onOpenChange(false)
    },
    onError: e => setError(e instanceof Error ? e.message : 'Something went wrong.'),
  })

  const list = groups.data ?? []
  const member = new Map(draft.map(g => [g.groupId, g]))
  const dirty = !sameMemberships(saved, draft)
  const needle = search.trim().toLowerCase()
  // Chosen groups first, then the rest, each by name.
  const shown = list
    .filter(g => !needle || g.name.toLowerCase().includes(needle) || g.teams.some(t => t.name.toLowerCase().includes(needle)))
    .sort((a, b) => Number(member.has(b.id)) - Number(member.has(a.id)) || a.name.localeCompare(b.name))
  const primary = list.find(g => member.get(g.id)?.isPrimary)
  const teams = [
    ...new Map(list.filter(g => member.has(g.id)).flatMap(g => g.teams).map(t => [t.id, t.name])).values(),
  ].sort()

  return (
    <Dialog open={person !== null} onOpenChange={onOpenChange}>
      <DialogContent className='gap-0 overflow-hidden p-0 sm:max-w-xl' onOpenAutoFocus={e => e.preventDefault()}>
        <DialogHeader className='space-y-1 border-b px-6 pb-4 pt-6 text-left'>
          <DialogTitle className='flex items-center gap-2.5'>
            <span className='flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary'>
              <Users className='h-4 w-4' />
            </span>
            Groups for {person?.name}
          </DialogTitle>
          <DialogDescription>
            Tap a group to add or remove it. The <Star className='inline h-3 w-3 fill-amber-400 text-amber-400' /> marks
            their Primary group.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-3 px-6 py-4'>
          {error && <p className='rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive'>{error}</p>}
          {groups.error && <p className='text-sm text-destructive'>{groups.error.message}</p>}

          {list.length > 6 && (
            <div className='relative'>
              <Search className='pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground' />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder='Find a group or team'
                aria-label='Find a group'
                className='h-9 rounded-lg pl-9'
              />
            </div>
          )}

          {groups.isLoading && <p className='py-6 text-center text-sm text-muted-foreground'>Loading groups…</p>}
          {groups.isSuccess && list.length === 0 && (
            <div className='rounded-xl border border-dashed px-4 py-8 text-center'>
              <p className='text-sm font-medium'>No groups yet</p>
              <p className='mt-1 text-xs text-muted-foreground'>Create one on the Groups tab first.</p>
            </div>
          )}

          <ul className='max-h-[50vh] space-y-1.5 overflow-y-auto pr-1'>
            {shown.map(g => {
              const m = member.get(g.id)
              return (
                <li
                  key={g.id}
                  className={cn(
                    'flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-all',
                    m ? 'border-primary/50 bg-primary/[0.04] shadow-sm' : 'hover:border-foreground/20 hover:bg-muted/40',
                  )}
                >
                  <button
                    type='button'
                    disabled={!canManage}
                    aria-pressed={!!m}
                    onClick={() => setDraft(toggleMembership(draft, g.id, !m))}
                    className='flex min-w-0 flex-1 items-center gap-3 text-left disabled:cursor-default'
                  >
                    <span
                      className={cn(
                        'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors',
                        m ? 'border-primary bg-primary text-primary-foreground' : 'bg-background',
                      )}
                    >
                      {m && <Check className='h-3.5 w-3.5' />}
                    </span>
                    <span className='min-w-0 flex-1'>
                      <span className='block truncate text-sm font-medium'>{g.name}</span>
                      <span className='mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground'>
                        <span className='flex items-center gap-1'>
                          <FileText className='h-3 w-3' /> {g.forms.length} {g.forms.length === 1 ? 'form' : 'forms'}
                        </span>
                        <span className='flex items-center gap-1'>
                          <Users className='h-3 w-3' /> {g.members.length}
                        </span>
                        {g.teams.map(t => (
                          <span key={t.id} className='flex items-center gap-1 text-indigo-600'>
                            <Network className='h-3 w-3' /> {t.name}
                          </span>
                        ))}
                      </span>
                    </span>
                  </button>
                  {m && (
                    <button
                      type='button'
                      disabled={!canManage || m.isPrimary}
                      onClick={() => setDraft(setPrimary(draft, g.id))}
                      title={m.isPrimary ? 'Primary group' : 'Make this their Primary group'}
                      aria-label={m.isPrimary ? `${g.name} is the Primary group` : `Make ${g.name} the Primary group`}
                      className={cn(
                        'flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
                        m.isPrimary
                          ? 'bg-amber-100 text-amber-800'
                          : 'text-muted-foreground hover:bg-amber-50 hover:text-amber-700',
                      )}
                    >
                      <Star className={cn('h-3.5 w-3.5', m.isPrimary && 'fill-amber-400 text-amber-500')} />
                      {m.isPrimary ? 'Primary' : 'Set primary'}
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        </div>

        <DialogFooter className='items-center gap-3 border-t bg-muted/30 px-6 py-3 sm:justify-between'>
          <p className='text-xs text-muted-foreground'>
            {draft.length === 0
              ? 'In no groups'
              : `${draft.length} ${draft.length === 1 ? 'group' : 'groups'}${primary ? ` · Primary ${primary.name}` : ''}`}
            {teams.length > 0 && ` · ${teams.length === 1 ? 'Team' : 'Teams'} ${teams.join(', ')}`}
          </p>
          {canManage ? (
            <div className='flex gap-2'>
              <Button variant='ghost' onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
                {save.isPending && <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />}
                Save
              </Button>
            </div>
          ) : (
            <Button variant='ghost' onClick={() => onOpenChange(false)}>
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
